/**
 * Professional Conversation Management Interface
 * 
 * A modern 3-panel conversation management system designed for scalability and professional use.
 * Features a 25% left sidebar for conversations, 50% middle panel for chat, and 25% right panel 
 * for customer intelligence and quick actions.
 * 
 * Architecture:
 * - Scalable component design with clear separation of concerns
 * - Real-time WebSocket integration with automatic connection management
 * - Professional design system with consistent styling
 * - Responsive layout that adapts to different screen sizes
 * - Extensive logging for debugging and monitoring
 * 
 * @version 1.0.0
 * @author CleverAct AI Team
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/Card'
import { Button } from '../../../ui/Button'
import { 
  Monitor, 
  Sparkles, 
  Users, 
  BarChart3,
  Settings,
  Maximize2,
  Minimize2,
  AlertCircle,
  Wifi,
  WifiOff,
  Zap,
  User
} from 'lucide-react'

import { logger } from '../../../../utils/logger'
import { useAuth } from '../../../../hooks/useAuth'
import { conversationHistoryService } from '../../../../services/conversationHistory'
import * as Types from '../../../../types'
import { useConversationList, useConversationHistory } from '../../../../hooks/useConversationHistory'
import { useUnreadTracking } from '../../../../services/unreadTracking'
import { useConversationUpdates } from '../../../../hooks/useRealTimeNotifications'
import { useRealTimeConversations } from '../../../../hooks/useRealTimeConversations'

// Import new professional components
import ChatScopeConversationSidebar from './ChatScopeConversationSidebar'
import CustomerIntelligencePanel from './CustomerIntelligencePanel'
import CleanChatInterface from './CleanChatInterface'
import ConversationDetail from '../ConversationDetail'
import { Loader2 } from 'lucide-react'

/**
 * Enhanced conversation assignment type with customer intelligence data
 */
export interface EnhancedConversationAssignment extends Types.BaseEntity {
  conversation_id: string
  title: string
  customer_name: string
  handoff_reason: string
  created_at: string
  status: string
  wait_time: number
  last_message?: string
  last_message_time?: string
  unread_count?: number
  priority?: string
  
  // Customer session data from ConversationSession model
  visitor_name?: string
  visitor_email?: string
  visitor_phone?: string
  device_type?: string
  browser_name?: string
  browser_version?: string
  operating_system?: string
  country?: string
  city?: string
  timezone?: string
  page_url?: string
  referrer_url?: string
  utm_source?: string
  session_duration?: number
  total_messages?: number
  satisfaction_rating?: number
  previous_visits?: number
  is_returning_visitor?: boolean
  is_active?: boolean
  is_connected_realtime?: boolean
  
  // Agent assignment information (from backend API)
  assigned_agent_id?: string
  assigned_agent_name?: string
  current_agent_id?: string
  current_agent_name?: string
}

/**
 * Customer intelligence data structure
 */
export interface CustomerIntelligence {
  profile: {
    name: string
    email?: string
    phone?: string
    location: string
    timezone?: string
    isOnline: boolean
  }
  device: {
    type: 'mobile' | 'desktop' | 'tablet' | 'unknown'
    browser: string
    os: string
    screen?: string
  }
  engagement: {
    score?: number
    satisfaction?: number
    currentPage?: string
    pagesViewed?: string[]
    timeOnSite?: number
    previousVisits: number
    isReturning: boolean
    engagementScore?: number
  }
  context: {
    currentPage?: string
    referrer?: string
    utmSource?: string
    entryPoint: string
  }
}

/**
 * Professional conversation view props
 */
export interface ProfessionalConversationViewProps {
  /** Custom CSS classes for styling */
  className?: string
  /** Real-time connection status from parent */
  isRealTimeConnected?: boolean
  /** Real-time connection state */
  realTimeConnectionState?: string
  /** Last real-time update timestamp */
  lastRealTimeUpdate?: Date
  /** Enable debugging mode with additional logging */
  debugMode?: boolean
  
  // Enhanced status system props
  /** Conversation status constants */
  conversationStatus?: any
  /** Status color palette */
  statusColors?: any
  /** Priority levels */
  priorityLevels?: any
  /** Function to map legacy status to new status */
  mapLegacyToNewStatus?: (legacyStatus: string, hasAgentResponse?: boolean) => string
  /** Function to calculate conversation priority */
  calculatePriority?: (conversation: any) => string
  /** StatusBadge component */
  StatusBadge?: React.ComponentType<{ status: string; priority: string; className?: string }>
}

/**
 * Panel size configuration for responsive design
 */
const PANEL_SIZES = {
  sidebar: {
    default: 'w-1/5',      // 20% width (optimized for more chat space)
    collapsed: 'w-16',      // Collapsed to icon bar
    expanded: 'w-1/4'       // Expanded but still compact
  },
  chat: {
    default: 'flex-1',      // Takes remaining space (60% with new distribution)
    sidebarCollapsed: 'flex-1', // Adjusts when sidebar collapsed
    customerPanelHidden: 'flex-1' // Adjusts when customer panel hidden
  },
  customer: {
    default: 'w-1/5',      // 20% width (optimized for more chat space)
    hidden: 'w-0',         // Hidden
    expanded: 'w-1/4'      // Expanded but still compact
  }
} as const

/**
 * Connection status configuration for real-time indicators
 */
const CONNECTION_STATUS = {
  connected: {
    icon: Zap,
    label: 'Live',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200'
  },
  connecting: {
    icon: Wifi,
    label: 'Connecting',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  disconnected: {
    icon: WifiOff,
    label: 'Offline',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  }
} as const

/**
 * Professional Conversation Management Interface
 * 
 * Main component that orchestrates the 3-panel layout and manages state flow
 * between conversation list, chat interface, and customer intelligence panel.
 */
export const ProfessionalConversationView: React.FC<ProfessionalConversationViewProps> = ({
  className = '',
  isRealTimeConnected = false,
  realTimeConnectionState = 'disconnected',
  lastRealTimeUpdate,
  debugMode = process.env.NODE_ENV === 'development',
  conversationStatus,
  statusColors,
  priorityLevels,
  mapLegacyToNewStatus,
  calculatePriority,
  StatusBadge,
}) => {
  
  // Authentication and user context (must be declared first)
  const { user } = useAuth()
  
  // Unread message tracking
  const { autoMarkAsReadOnOpen } = useUnreadTracking()
  
  // Component state management (must be declared before hooks that reference them)
  const [selectedConversation, setSelectedConversation] = useState<EnhancedConversationAssignment | null>(null)
  
  // 🎯 Local state for immediate status updates without full refetch (declared early for hook dependency)
  const [localStatusOverride, setLocalStatusOverride] = useState<{conversationId: string, status: string, assignedAgentName?: string | null, timestamp?: number} | null>(null)
  
  // Conversation list with real-time updates
  const { conversations, refetch: refetchConversations } = useConversationList(30000)
  
  // Real-time conversation updates
  const { conversations: conversationUpdates, lastUpdatedConversation } = useConversationUpdates()
  
  // Detailed conversation data fetching
  // Disable frequent polling when we have fresh real-time data to prevent conflicts
  const hasRecentRealTimeUpdate = localStatusOverride?.timestamp && (Date.now() - localStatusOverride.timestamp < 60000) // Fresh if less than 1 minute old
  
  const {
    conversation: fetchedFullConversation,
    isLoading: isConversationLoading,
    error: conversationFetchError,
    refetch: refetchDetailedConversation,
  } = useConversationHistory({
    conversationId: selectedConversation?.conversation_id,
    autoFetch: true,
    includeProfile: true,
    pollInterval: hasRecentRealTimeUpdate ? 300000 : 60000, // Slower polling (5min) when we have fresh real-time data, otherwise 1min
  })
  const [customerIntelligence, setCustomerIntelligence] = useState<CustomerIntelligence | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [customerPanelHidden, setCustomerPanelHidden] = useState(false)
  const [viewMode, setViewMode] = useState<'standard' | 'focus' | 'analytics'>('standard')
  const [agents, setAgents] = useState<any[]>([])
  const [isLoadingAgents, setIsLoadingAgents] = useState(false)
  const [detailedConversation, setDetailedConversation] = useState<any>(null)
  const [customerProfile, setCustomerProfile] = useState<any>(null)
  
  // Clear local status override when conversation changes, but preserve it for the same conversation
  useEffect(() => {
    // Only clear if the conversation ID actually changed, not if the same conversation was updated
    if (localStatusOverride && localStatusOverride.conversationId !== selectedConversation?.conversation_id) {
      setLocalStatusOverride(null)
    }
  }, [selectedConversation?.conversation_id, localStatusOverride])
  
  // Loading and error states for conversation selection
  const [isLoadingConversation, setIsLoadingConversation] = useState(false)
  const [conversationError, setConversationError] = useState<string | null>(null)
  
  // Ref to track the currently loading conversation to prevent race conditions
  const currentlyLoadingConversationRef = useRef<string | null>(null)

  // Component initialization logging
  useEffect(() => {
    logger.info('🚀 ProfessionalConversationView initialized', {
      component: 'ProfessionalConversationView',
      realTimeConnected: isRealTimeConnected,
      connectionState: realTimeConnectionState,
      debugMode
    })
  }, [])

  // Load available agents for assignment
  useEffect(() => {
    const loadAgents = async () => {
      const userRole = user?.role || user?.user_type || localStorage.getItem('user_type')
      
      if (!user || (userRole !== 'company_owner' && userRole !== 'admin' && userRole !== 'agent')) {
        logger.info('👤 User does not have agent management permissions, skipping agent load', {
          component: 'ProfessionalConversationView',
          userRole,
          allowedRoles: ['company_owner', 'admin', 'agent']
        })
        return
      }
      
      try {
        setIsLoadingAgents(true)
        
        logger.info('🔍 Loading available agents for Professional interface', {
          component: 'ProfessionalConversationView'
        })
        
        const agentList = await conversationHistoryService.getAvailableAgents()
        
        setAgents(agentList)
        logger.info('✅ Loaded agents for Professional interface', {
          component: 'ProfessionalConversationView',
          agentCount: agentList.length
        })
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load agents'
        logger.error('❌ Error loading agents in Professional interface', {
          component: 'ProfessionalConversationView',
          error: errorMessage
        })
        
      } finally {
        setIsLoadingAgents(false)
      }
    }
    
    loadAgents()
  }, [user])


  // Handle real-time conversation updates from WebSocket notifications
  useEffect(() => {
    if (!selectedConversation || !lastUpdatedConversation) return
    
    // Check if the currently selected conversation was updated
    if (selectedConversation.conversation_id === lastUpdatedConversation) {
      const updates = conversationUpdates.get(lastUpdatedConversation)
      
      if (updates) {
        // Check if there are actual changes to prevent unnecessary updates
        const hasStatusChange = updates.status && updates.status !== selectedConversation.status
        const hasAgentChange = updates.assigned_agent_id !== selectedConversation.assigned_agent_id
        const hasAgentNameChange = updates.assigned_agent_name !== selectedConversation.assigned_agent_name
        
        // Special handling for resolved conversations - always apply the update  
        const isResolvedUpdate = updates.status === 'resolved'
        
        // 🐛 DEBUG: Enhanced logging for close chat debugging
        if (isResolvedUpdate) {
          console.warn('🐛 DEBUG: Processing resolved conversation update in ProfessionalConversationView', {
            conversationId: lastUpdatedConversation,
            selectedConversationId: selectedConversation.conversation_id,
            updatesReceived: updates,
            currentStatus: selectedConversation.status,
            newStatus: updates.status,
            hasStatusChange,
            isResolvedUpdate
          })
        }
        
        if (hasStatusChange || hasAgentChange || hasAgentNameChange || isResolvedUpdate) {
          logger.info('🔄 Applying real-time updates to selected conversation', {
            component: 'ProfessionalConversationView',
            conversationId: lastUpdatedConversation,
            hasStatusChange,
            hasAgentChange,
            hasAgentNameChange,
            isResolvedUpdate,
            newStatus: updates.status,
            oldStatus: selectedConversation.status
          })
          
          // Update the selected conversation with real-time data
          const updatedConversation = {
            ...selectedConversation,
            ...updates,
            // Ensure status is properly updated - fix for close chat status not updating
            status: updates.status !== undefined ? updates.status : selectedConversation.status,
            assigned_agent_id: updates.assigned_agent_id !== undefined ? updates.assigned_agent_id : selectedConversation.assigned_agent_id,
            assigned_agent_name: updates.assigned_agent_name !== undefined ? updates.assigned_agent_name : selectedConversation.assigned_agent_name
          }
          
          setSelectedConversation(updatedConversation as EnhancedConversationAssignment)
          
          // Update local status override to ensure ConversationDetail header updates immediately
          // Set with a timestamp to track when this was last updated
          setLocalStatusOverride({
            conversationId: lastUpdatedConversation,
            status: updatedConversation.status,
            assignedAgentName: updatedConversation.assigned_agent_name,
            timestamp: Date.now() // Add timestamp to track freshness
          })
          
          logger.info('✅ Updated selected conversation with real-time data via WebSocket', {
            component: 'ProfessionalConversationView',
            conversationId: lastUpdatedConversation,
            newStatus: updatedConversation.status,
            assignedAgent: updatedConversation.assigned_agent_name,
            previousAgent: selectedConversation.assigned_agent_name,
            localOverrideSet: true
          })
        } else {
          logger.debug('🔄 Skipping update - no actual changes detected', {
            component: 'ProfessionalConversationView',
            conversationId: lastUpdatedConversation
          })
        }
      }
    }
  }, [conversationUpdates, lastUpdatedConversation]) // Remove selectedConversation from dependencies to prevent infinite loop

  // 🔔 Listen for new conversation assignments (real-time popup)
  useEffect(() => {
    const handleNewConversationAssignment = (event: CustomEvent) => {
      const { conversationId, conversationData, timestamp } = event.detail
      
      logger.info('🆕 New conversation assigned via WebSocket', {
        component: 'ProfessionalConversationView',
        conversationId,
        visitorName: conversationData.visitor_name,
        handoffReason: conversationData.handoff_reason,
        timestamp
      })
      
      // Real-time WebSocket will automatically update the conversation list
      logger.info('🔄 New conversation assignment - WebSocket will update UI automatically')
      
      // TODO: You might want to add a toast notification here
      // toast.success(`New conversation assigned: ${conversationData.visitor_name || 'Anonymous Customer'}`)
    }

    const handleConversationHandoff = (event: CustomEvent) => {
      const { conversationId, fromType, handoffData, timestamp } = event.detail
      
      logger.info('🔄 Conversation handoff received via WebSocket', {
        component: 'ProfessionalConversationView',
        conversationId,
        fromType,
        timestamp
      })
      
      // Real-time WebSocket will automatically update the conversation list
      logger.info('🔄 Handoff created - WebSocket will update UI automatically')
    }

    // Add event listeners
    window.addEventListener('newConversationAssigned', handleNewConversationAssignment as EventListener)
    window.addEventListener('conversationHandoff', handleConversationHandoff as EventListener)
    
    // Cleanup
    return () => {
      window.removeEventListener('newConversationAssigned', handleNewConversationAssignment as EventListener)
      window.removeEventListener('conversationHandoff', handleConversationHandoff as EventListener)
    }
  }, []) // Empty dependency array - only set up once

  // Track last real-time update timestamp for UI indicators
  const [realTimeUpdateTimestamp, setRealTimeUpdateTimestamp] = useState<Date | null>(null)
  
  // Use the real-time conversations hook
  const { 
    subscribeToConversation, 
    unsubscribeFromConversation 
  } = useRealTimeConversations({
    onConversationUpdate: (conversationId, data) => {
      logger.info('📡 Real-time conversation update received', {
        component: 'ProfessionalConversationView',
        conversationId,
        data
      })
      
      // Update last real-time update timestamp for UI indicators
      setRealTimeUpdateTimestamp(new Date())
      
      // Check if this update contains user presence information
      if (data) {
        const isConnected = 'is_connected_realtime' in data ? !!data.is_connected_realtime : ('is_active' in data ? !!data.is_active : null);
        
        if (isConnected !== null) {
          logger.info('👤 User presence update received', {
            component: 'ProfessionalConversationView',
            conversationId,
            isConnected: isConnected,
            source: 'is_connected_realtime' in data ? 'realtime' : 'legacy'
          })
          
          // Update customer intelligence with real-time presence data
          setCustomerIntelligence(prevState => {
            if (!prevState) return prevState
            
            return {
              ...prevState,
              profile: {
                ...prevState.profile,
                isOnline: isConnected
              }
            }
          })
        }
      }
    }
  })
  
  // Subscribe to real-time updates for the selected conversation
  useEffect(() => {
    if (!selectedConversation?.conversation_id) return
    
    // Subscribe to updates for this conversation
    subscribeToConversation(selectedConversation.conversation_id)
    
    return () => {
      // Unsubscribe when component unmounts or conversation changes
      if (selectedConversation?.conversation_id) {
        unsubscribeFromConversation(selectedConversation.conversation_id)
      }
    }
  }, [selectedConversation?.conversation_id, subscribeToConversation, unsubscribeFromConversation])

  /**
   * Refetch conversation data (used when same conversation is selected again)
   */
  const refetchConversationData = useCallback(async (conversationId: string) => {
    if (!conversationId || isLoadingConversation) return
    
    logger.info('🔄 Refetching conversation data', {
      component: 'ProfessionalConversationView',
      conversationId
    })
    
    try {
      setIsLoadingConversation(true)
      setConversationError(null)
      
      // Fetch fresh conversation data from the single source of truth: the history endpoint
      const conversationHistory = await conversationHistoryService.getConversationHistory(conversationId)
      
      // Update existing conversation with fresh data
      setDetailedConversation(conversationHistory)
      setCustomerProfile(conversationHistory.customer_profile)
      
      // Update selectedConversation with the latest status and assignment info from the history
      if (conversationHistory) {
        // Merge with existing data to preserve properties not in the history response
        setSelectedConversation(prev => ({
          ...prev,
          ...conversationHistory
        }) as EnhancedConversationAssignment)
        
        logger.info('✅ Updated selectedConversation with fresh status from history API', {
          component: 'ProfessionalConversationView',
          conversationId,
          newStatus: conversationHistory.status,
          assignedAgent: conversationHistory.assigned_agent_name
        })
      }
      
      logger.info('✅ Conversation data refetched successfully', {
        component: 'ProfessionalConversationView',
        conversationId
      })
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refetch conversation'
      logger.error('❌ Error refetching conversation data', {
        component: 'ProfessionalConversationView',
        conversationId,
        error: errorMessage
      })
      setConversationError(errorMessage)
    } finally {
      setIsLoadingConversation(false)
    }
  }, [isLoadingConversation])

  /**
   * Calculate customer engagement score based on available metrics
   */
  const calculateEngagementScore = useCallback((conversation: EnhancedConversationAssignment): number => {
    let score = 0
    
    // Message engagement (0-40 points)
    const messageCount = conversation.total_messages || 0
    score += Math.min(messageCount * 2, 40)
    
    // Session duration (0-30 points)
    const duration = conversation.session_duration || 0
    score += Math.min(duration / 60, 30) // 1 point per minute, max 30
    
    // Returning visitor bonus (0-20 points)
    if (conversation.is_returning_visitor) {
      const visits = conversation.previous_visits || 0
      score += Math.min(visits * 2, 20)
    }
    
    // Satisfaction rating bonus (0-10 points)
    if (conversation.satisfaction_rating) {
      score += conversation.satisfaction_rating * 2
    }
    
    return Math.min(Math.round(score), 100)
  }, [])

  /**
   * Handle conversation selection with enhanced logging and customer intelligence extraction
   * Implements race condition protection and improved state management
   */
  const handleConversationSelect = useCallback(async (conversation: EnhancedConversationAssignment) => {
    // Prevent multiple rapid clicks and race conditions
    if (isLoadingConversation) {
      logger.info('⏳ Conversation selection already in progress, ignoring click', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id
      })
      return
    }
    
    // Handle re-selection of the same conversation (refresh data only if no fresh WebSocket data)
    if (selectedConversation?.conversation_id === conversation.conversation_id) {
      const hasRecentWebSocketData = localStatusOverride?.timestamp && (Date.now() - localStatusOverride.timestamp < 30000) // Fresh if less than 30 seconds old
      
      if (hasRecentWebSocketData) {
        logger.info('🔄 Same conversation re-selected, but have fresh WebSocket data - skipping refetch', {
          component: 'ProfessionalConversationView',
          conversationId: conversation.conversation_id,
          webSocketDataAge: localStatusOverride?.timestamp ? Date.now() - localStatusOverride.timestamp : 'none'
        })
        return
      }
      
      logger.info('🔄 Same conversation re-selected, refreshing data', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id
      })
      await refetchConversationData(conversation.conversation_id)
      return
    }
    
    logger.info('📋 Professional conversation selected', {
      component: 'ProfessionalConversationView',
      conversationId: conversation.conversation_id,
      customerName: conversation.visitor_name || 'Anonymous',
      status: conversation.status,
      waitTime: conversation.wait_time,
      deviceType: conversation.device_type
    })

    // Set the ref to track currently loading conversation
    currentlyLoadingConversationRef.current = conversation.conversation_id

    // Start loading state immediately and clear previous conversation
    // This prevents white/blank screen during data fetching
    setIsLoadingConversation(true)
    setConversationError(null)

    // IMPORTANT: Clear customer intelligence and detailed data before fetching new conversation
    // This prevents stale data from previous conversation from showing during load
    setCustomerIntelligence(null)
    setDetailedConversation(null)
    setCustomerProfile(null)

    try {
      // Set selected conversation immediately for UI responsiveness
      // If we have fresh WebSocket data for this conversation, merge it with the conversation data
      const hasRecentWebSocketData = localStatusOverride?.timestamp &&
                                    localStatusOverride.conversationId === conversation.conversation_id &&
                                    (Date.now() - localStatusOverride.timestamp < 30000) // Fresh if less than 30 seconds old

      const enhancedConversation = hasRecentWebSocketData ? {
        ...conversation,
        status: localStatusOverride.status,
        assigned_agent_name: localStatusOverride.assignedAgentName
      } : conversation

      setSelectedConversation(enhancedConversation)
      
      logger.info('📋 Setting selected conversation with WebSocket enhancement', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id,
        hasWebSocketData: hasRecentWebSocketData,
        originalAssignedAgent: conversation.assigned_agent_name,
        enhancedAssignedAgent: enhancedConversation.assigned_agent_name
      })

      // Fetch detailed conversation history and customer profile, which is the single source of truth
      logger.info('FETCHING SINGLE SOURCE OF TRUTH: getConversationHistory',
        { component: 'ProfessionalConversationView', conversationId: conversation.conversation_id }
      );
      const authoritativeHistoryData = await conversationHistoryService.getConversationHistory(conversation.conversation_id);

      // Check if conversation selection changed during the async operation
      if (currentlyLoadingConversationRef.current !== conversation.conversation_id) {
        logger.info('🔄 Conversation selection changed during fetch, aborting update', { originalId: conversation.conversation_id, newId: currentlyLoadingConversationRef.current });
        return;
      }

      // Now, update the state with the authoritative data from the history endpoint
      const authoritativeConversation = {
        ...conversation, // Start with sidebar data
        ...authoritativeHistoryData, // Override with fresh, detailed data
      };

      setSelectedConversation(authoritativeConversation as EnhancedConversationAssignment);
      
      // Immediately set basic customer intelligence from conversation data
      const basicIntelligence: CustomerIntelligence = {
        profile: {
          name: conversation.visitor_name || 'Anonymous User',
          email: conversation.visitor_email,
          phone: conversation.visitor_phone,
          location: conversation.city && conversation.country 
            ? `${conversation.city}, ${conversation.country}` 
            : 'Unknown location',
          timezone: conversation.timezone,
          isOnline: conversation.is_connected_realtime || conversation.is_active || false
        },
        device: {
          type: (conversation.device_type?.toLowerCase() as any) || 'unknown',
          browser: conversation.browser_name && conversation.browser_version
            ? `${conversation.browser_name} ${conversation.browser_version}`
            : 'Unknown browser',
          os: conversation.operating_system || 'Unknown OS'
        },
        engagement: {
          previousVisits: conversation.previous_visits || 0,
          isReturning: conversation.is_returning_visitor || false,
          engagementScore: calculateEngagementScore(conversation)
        },
        context: {
          currentPage: conversation.page_url,
          referrer: conversation.referrer_url,
          utmSource: conversation.utm_source,
          entryPoint: conversation.referrer_url ? 'External' : 'Direct'
        }
      }
      
      const basicProfile = {
        visitor_name: conversation.visitor_name,
        visitor_email: conversation.visitor_email,
        visitor_phone: conversation.visitor_phone,
        browser_name: conversation.browser_name,
        browser_version: conversation.browser_version,
        device_type: conversation.device_type,
        operating_system: conversation.operating_system,
        city: conversation.city,
        country: conversation.country,
        timezone: conversation.timezone,
        is_active: conversation.is_active,
        is_connected_realtime: conversation.is_connected_realtime
      }
      
      // Batch state updates for better performance and consistency
      logger.info('🔄 Setting basic customer intelligence', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id,
        customerName: basicIntelligence.profile.name,
        aboutToSetIntelligence: true
      })
      
      // Use React 18's automatic batching - all state updates will be batched
      setCustomerIntelligence(basicIntelligence)
      setDetailedConversation(conversation)
      setCustomerProfile(basicProfile)
      
      logger.info('✅ Immediate customer data set', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id,
        hasIntelligence: !!basicIntelligence,
        customerName: basicIntelligence.profile.name
      })
      
      // 📖 Mark conversation as read when selected (clears unread count badge)
      autoMarkAsReadOnOpen(conversation.conversation_id).then(() => {
        logger.info('✅ Conversation marked as read', {
          component: 'ProfessionalConversationView',
          conversationId: conversation.conversation_id,
          action: 'mark_as_read_success'
        })
      }).catch((error) => {
        logger.error('❌ Failed to mark conversation as read', {
          component: 'ProfessionalConversationView',
          conversationId: conversation.conversation_id,
          error: error instanceof Error ? error.message : 'Unknown error'
        })
      })

      // Fetch detailed conversation history and customer profile
      logger.info('🔍 Fetching detailed customer data for Professional interface', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id
      })

      // Fetch conversation history which contains the customer profile data
      const conversationHistory = await conversationHistoryService.getConversationHistory(conversation.conversation_id)
      
      // Check if conversation selection changed during the async operation using ref
      if (currentlyLoadingConversationRef.current !== conversation.conversation_id) {
        logger.info('🔄 Conversation selection changed during fetch, aborting', {
          component: 'ProfessionalConversationView',
          originalId: conversation.conversation_id,
          currentlyLoadingId: currentlyLoadingConversationRef.current
        })
        return
      }
      
      let detailedConversation = conversationHistory
      let detailedProfile = conversationHistory.customer_profile

      logger.info('✅ Retrieved detailed conversation data for Professional interface', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id,
        hasCustomerProfile: !!detailedProfile,
        customerBrowser: detailedProfile?.browser_name || 'Unknown',
        customerLocation: `${detailedProfile?.city || 'Unknown'}, ${detailedProfile?.country || 'Unknown'}`
      })

      // Update with detailed data (merge with existing basic data) - batch these updates
      // 🔧 FIX: Prioritize data sources correctly: WebSocket > API > Sidebar
      const updatedDetailedConversation = {
        ...conversation,
        ...detailedConversation,
        // Prioritize: localStatusOverride (WebSocket) > detailedConversation (API) > conversation (sidebar)
        status: localStatusOverride?.status || detailedConversation?.status || conversation.status,
        assigned_agent_name: localStatusOverride?.assignedAgentName || detailedConversation?.assigned_agent_name || conversation.assigned_agent_name,
        assigned_agent_id: detailedConversation?.assigned_agent_id || conversation.assigned_agent_id
      }
      
      const updatedProfile = {
        ...basicProfile,
        ...detailedProfile
      }

      // Process enhanced customer intelligence from the detailed data
      const enhancedIntelligence: CustomerIntelligence = {
        profile: {
          name: detailedProfile?.visitor_name || conversation.visitor_name || 'Anonymous Visitor',
          email: detailedProfile?.visitor_email || conversation.visitor_email,
          phone: detailedProfile?.visitor_phone || conversation.visitor_phone,
          location: detailedProfile?.location || 
            (detailedProfile?.city && detailedProfile?.country 
              ? `${detailedProfile.city}, ${detailedProfile.country}` 
              : conversation.city && conversation.country 
                ? `${conversation.city}, ${conversation.country}` 
                : 'Unknown location'),
          timezone: detailedProfile?.timezone || conversation.timezone,
          isOnline: detailedProfile?.is_connected_realtime || detailedProfile?.is_active || conversation.is_connected_realtime || conversation.is_active || false
        },
        device: {
          type: (detailedProfile?.device_type?.toLowerCase() as any) || (conversation.device_type?.toLowerCase() as any) || 'unknown',
          browser: detailedProfile?.browser || 
            (detailedProfile?.browser_name && detailedProfile?.browser_version
              ? `${detailedProfile.browser_name} ${detailedProfile.browser_version}`
              : conversation.browser_name && conversation.browser_version
                ? `${conversation.browser_name} ${conversation.browser_version}`
                : 'Unknown browser'),
          os: detailedProfile?.operating_system || conversation.operating_system || 'Unknown OS'
        },
        engagement: {
          sessionDuration: detailedProfile?.session_duration || conversation.session_duration || 0,
          messageCount: detailedConversation?.total_messages || conversation.total_messages || 0,
          satisfaction: detailedProfile?.satisfaction_rating || conversation.satisfaction_rating,
          previousVisits: detailedProfile?.previous_visits || conversation.previous_visits || 0,
          isReturning: detailedProfile?.is_returning_visitor || conversation.is_returning_visitor || false,
          engagementScore: calculateEngagementScore({
            ...conversation,
            total_messages: detailedConversation?.total_messages,
            session_duration: detailedProfile?.session_duration,
            satisfaction_rating: detailedProfile?.satisfaction_rating,
            previous_visits: detailedProfile?.previous_visits,
            is_returning_visitor: detailedProfile?.is_returning_visitor
          })
        },
        context: {
          currentPage: detailedProfile?.current_page || detailedProfile?.page_url || conversation.page_url,
          referrer: detailedProfile?.referrer_url || conversation.referrer_url,
          utmSource: detailedProfile?.utm_source || conversation.utm_source,
          entryPoint: (detailedProfile?.referrer_url || conversation.referrer_url) ? 'External' : 'Direct'
        }
      }

      // Batch all state updates for consistency and performance
      // 🔧 FIX: Update selectedConversation with the preserved WebSocket data
      setSelectedConversation(updatedDetailedConversation as EnhancedConversationAssignment)
      setDetailedConversation(updatedDetailedConversation)
      setCustomerProfile(updatedProfile)
      setCustomerIntelligence(enhancedIntelligence)

      logger.info('✅ Customer intelligence updated with detailed data', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id,
        preservedWebSocketData: hasRecentWebSocketData,
        finalAssignedAgent: updatedDetailedConversation.assigned_agent_name,
        originalAssignedAgent: conversation.assigned_agent_name,
        apiResponseAgent: detailedConversation?.assigned_agent_name,
        webSocketAssignedAgent: localStatusOverride?.assignedAgentName,
        assignmentDataFlow: {
          fromSidebar: conversation.assigned_agent_name,
          fromAPI: detailedConversation?.assigned_agent_name,
          fromWebSocket: localStatusOverride?.assignedAgentName,
          finalResult: updatedDetailedConversation.assigned_agent_name,
          dataPriority: 'WebSocket > API > Sidebar'
        },
        intelligence: {
          name: enhancedIntelligence.profile.name,
          browser: enhancedIntelligence.device.browser,
          deviceType: enhancedIntelligence.device.type,
          location: enhancedIntelligence.profile.location,
          os: enhancedIntelligence.device.os
        }
      })

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logger.error('❌ Error fetching detailed customer data for Professional interface', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id,
        error: errorMessage
      })

      // Set error state for user feedback
      setConversationError(errorMessage)

      // Fallback to basic conversation data if detailed fetch fails
      logger.info('🔄 Using fallback basic conversation data', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id
      })
      
      // Keep the basic intelligence that was already set
      // This ensures the UI still shows useful information even if detailed fetch fails
    } finally {
      // Clear the ref when loading completes (success or error)
      if (currentlyLoadingConversationRef.current === conversation.conversation_id) {
        currentlyLoadingConversationRef.current = null
      }
      
      // Always clear loading state
      setIsLoadingConversation(false)
      
      logger.info('🏁 Conversation selection completed', {
        component: 'ProfessionalConversationView',
        conversationId: conversation.conversation_id,
        hasError: false // Remove direct conversationError reference
      })
    }
  }, [
    isLoadingConversation, 
    selectedConversation, 
    refetchConversationData, 
    calculateEngagementScore, 
    autoMarkAsReadOnOpen
    // Removed conversationError to prevent callback recreation
  ])


  


  /**
   * Handle priority update from CustomerIntelligencePanel
   */
  const handlePriorityUpdate = useCallback((conversationId: string, oldPriority: string, newPriority: string) => {
    logger.debug('🎯 Priority update initiated from UI', {
      component: 'ProfessionalConversationView',
      conversationId,
      oldPriority,
      newPriority
    })

    // Update the selected conversation's priority immediately for UI responsiveness
    if (selectedConversation && selectedConversation.conversation_id === conversationId) {
      const updatedConversation = { ...selectedConversation, priority: newPriority }
      setSelectedConversation(updatedConversation as Types.ConversationAssignment)
      
      logger.debug('✅ Local conversation state updated with new priority', {
        component: 'ProfessionalConversationView',
        conversationId,
        newPriority
      })
    }

    // ✨ WebSocket notifications will handle the rest automatically!
    // The backend now sends real-time priority updates via WebSocket,
    // so the conversation list will be updated without manual refresh
    logger.info('📡 Priority update sent to API - WebSocket will broadcast updates to all connected users')
  }, [selectedConversation])

  /**
   * Handle layout changes and view mode switches
   */
  const handleLayoutChange = useCallback((action: 'toggleSidebar' | 'toggleCustomerPanel' | 'toggleFullscreen' | 'changeViewMode', value?: any) => {
    logger.info('🎨 Layout change requested', {
      component: 'ProfessionalConversationView',
      action,
      value
    })

    switch (action) {
      case 'toggleSidebar':
        setSidebarCollapsed(!sidebarCollapsed)
        break
      case 'toggleCustomerPanel':
        setCustomerPanelHidden(!customerPanelHidden)
        break
      case 'toggleFullscreen':
        setIsFullscreen(!isFullscreen)
        break
      case 'changeViewMode':
        setViewMode(value)
        break
    }
  }, [sidebarCollapsed, customerPanelHidden, isFullscreen])

  /**
   * Real-time connection status configuration
   */
  const connectionStatus = useMemo(() => {
    // Debug log the actual values
    console.log('🔍 ProfessionalConversationView connection status:', {
      isRealTimeConnected,
      realTimeConnectionState,
      timestamp: new Date().toISOString()
    })
    
    // Primary: Use the actual WebSocket connectionState
    if (realTimeConnectionState === 'connected') return CONNECTION_STATUS.connected
    if (realTimeConnectionState === 'connecting') return CONNECTION_STATUS.connecting
    
    // Secondary: Use isRealTimeConnected boolean (for compatibility)
    if (isRealTimeConnected && realTimeConnectionState !== 'disconnected') {
      return CONNECTION_STATUS.connected
    }
    
    return CONNECTION_STATUS.disconnected
  }, [isRealTimeConnected, realTimeConnectionState])

  /**
   * Dynamic panel sizing based on current layout state
   */
  const panelSizing = useMemo(() => {
    return {
      sidebar: sidebarCollapsed ? PANEL_SIZES.sidebar.collapsed : PANEL_SIZES.sidebar.default,
      chat: PANEL_SIZES.chat.default,
      customer: customerPanelHidden ? PANEL_SIZES.customer.hidden : PANEL_SIZES.customer.default
    }
  }, [sidebarCollapsed, customerPanelHidden])

  // User permission validation
  if (!user) {
    logger.warn('⚠️ Unauthorized access attempt to Professional Conversation View', {
      component: 'ProfessionalConversationView'
    })
    
    return (
      <div className="flex items-center justify-center h-full bg-slate-50">
        <Card className="w-96">
          <CardContent className="flex flex-col items-center p-8">
            <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Authentication Required</h3>
            <p className="text-sm text-gray-600 text-center">
              Please log in to access the professional conversation management interface.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const userRole = user.role || user.user_type || localStorage.getItem('user_type')
  
  // Allow company owners, admins, and agents to access Professional interface
  const hasAccess = userRole === 'company_owner' || userRole === 'admin' || userRole === 'agent'
  
  if (!hasAccess) {
    logger.warn('⚠️ Insufficient permissions for Professional Conversation View', {
      component: 'ProfessionalConversationView',
      userRole,
      userId: user.id
    })
    
    return (
      <div className="flex items-center justify-center h-full bg-slate-50">
        <Card className="w-96">
          <CardContent className="flex flex-col items-center p-8">
            <Users className="w-12 h-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h3>
            <p className="text-sm text-gray-600 text-center mb-2">
              This interface is available for company owners, administrators, and agents.
            </p>
            <p className="text-xs text-gray-500">
              Current role: {userRole} | User: {user.email}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className={`
      flex flex-col h-full bg-slate-50
      ${isFullscreen ? 'fixed inset-0 z-50' : ''}
      ${className}
    `}>
      
      {/* Professional Header Bar */}
      <Card className="rounded-none border-0 border-b border-slate-200 shadow-sm">
        <CardHeader className="py-3 px-6">
          <div className="flex items-center justify-between">
            
            {/* Left: Title and Connection Status */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <Monitor className="w-6 h-6 text-slate-700" />
                <div>
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Professional Conversations
                  </CardTitle>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Advanced conversation management with customer intelligence
                  </p>
                </div>
              </div>
              
            </div>
            
            {/* Right: Layout Controls */}
            <div className="flex items-center gap-2">
              
              {/* Sidebar Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleLayoutChange('toggleSidebar')}
                className="h-8 w-8 p-0"
                title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
              >
                {sidebarCollapsed ? <Users className="w-4 h-4" /> : <Users className="w-4 h-4" />}
              </Button>
              
              {/* Layout Controls */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleLayoutChange('toggleFullscreen')}
                className="h-8 w-8 p-0"
                title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                title="Settings"
              >
                <Settings className="w-4 h-4" />
              </Button>
              
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main 3-Panel Layout */}
      <div className="flex flex-1 min-h-0">
        
        {/* Left Panel - ChatScope-style Conversation Sidebar (25% or collapsed) */}
        {!sidebarCollapsed && (
          <div className={`${panelSizing.sidebar} min-w-0 flex-shrink-0 border-r border-slate-200`}>
            <ChatScopeConversationSidebar
              selectedConversationId={selectedConversation?.conversation_id}
              onConversationSelect={handleConversationSelect}
              isRealTimeConnected={isRealTimeConnected}
              realTimeConnectionState={realTimeConnectionState}
              lastRealTimeUpdate={lastRealTimeUpdate}
              viewMode={viewMode}
              selectedConversation={selectedConversation}
              currentUser={user}
              onQuickActionComplete={() => {
                logger.info('🎯 Quick action completed from sidebar', {
                  component: 'ProfessionalConversationView',
                  conversationId: selectedConversation?.conversation_id
                })
                // Real-time WebSocket notifications will handle the UI updates automatically
              }}
              className="h-full"
              conversationStatus={conversationStatus}
              statusColors={statusColors}
              priorityLevels={priorityLevels}
              mapLegacyToNewStatus={mapLegacyToNewStatus}
              calculatePriority={calculatePriority}
              StatusBadge={StatusBadge}
            />
          </div>
        )}
        
        {/* Middle Panel - Chat Interface (50% flexible) */}
        <div className={`${panelSizing.chat} min-w-0 flex flex-col bg-white`}>
          {selectedConversation ? (
            // Show loading if: (1) explicitly loading OR (2) don't have customer data yet
            // This prevents white screen flash when switching conversations
            isLoadingConversation || !customerIntelligence ? (
              // Loading state for conversation data
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Loading Conversation</h3>
                  <p className="text-sm text-gray-600">Fetching customer data and chat history...</p>
                  {conversationError && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg max-w-md mx-auto">
                      <p className="text-sm text-red-600">{conversationError}</p>
                      <button
                        onClick={() => refetchConversationData(selectedConversation.conversation_id)}
                        className="mt-2 text-xs text-red-700 hover:text-red-800 underline"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : viewMode === 'analytics' ? (
              // Analytics view for conversation
              <div className="flex-1 p-6">
                <div className="text-center text-gray-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">Conversation Analytics</h3>
                  <p className="text-sm">Advanced analytics view coming soon...</p>
                </div>
              </div>
            ) : (
              // Chat interface with customer details hidden (shown in right panel)
              <ConversationDetail
                conversation={selectedConversation}
                fullConversation={
                  // 🔧 FIX: Enhanced data merging - WebSocket data takes priority
                  (() => {
                    const base = fetchedFullConversation || {}
                    const webSocketData = selectedConversation || {}
                    const localOverride = localStatusOverride?.conversationId === selectedConversation?.conversation_id ? localStatusOverride : null
                    
                    return {
                      ...base, // API data (messages, customer_profile, etc.)
                      ...webSocketData, // WebSocket real-time updates
                      // Ensure real-time fields use most current data
                      status: localOverride?.status || base?.status || webSocketData?.status,
                      assigned_agent_name: localOverride?.assignedAgentName !== undefined 
                        ? localOverride.assignedAgentName 
                        : base?.assigned_agent_name || webSocketData?.assigned_agent_name,
                      assigned_agent_id: webSocketData?.assigned_agent_id || base?.assigned_agent_id
                    }
                  })()
                }
                agents={agents}
                isLoading={isConversationLoading}
                onAssignmentComplete={(result?: any) => {
                  logger.info('🎯 Assignment completed in Professional interface', {
                    component: 'ProfessionalConversationView',
                    conversationId: selectedConversation?.conversation_id,
                    result
                  })
                  
                  // 🎯 OPTIMIZED: Update status locally for immediate header update
                  if (result?.status && selectedConversation?.conversation_id) {
                    setLocalStatusOverride({
                      conversationId: selectedConversation.conversation_id,
                      status: result.status,
                      // 🔧 FIX: Use the newly assigned agent name from the result
                      assignedAgentName: result.status === 'ai_active' || result.status === 'resolved' ? null : result.agentName || result.assignedAgentName || selectedConversation.assigned_agent_name,
                      timestamp: Date.now()
                    })
                  }
                  
                  // Delay the conversation list refresh to allow WebSocket updates to arrive first
                  setTimeout(() => {
                    refetchConversations() // Refresh the sidebar list
                  }, 1000) // 1 second delay
                }}
                onCustomerDataUpdate={() => {}} // No-op since parent handles data now
                onPriorityUpdate={handlePriorityUpdate}
                className="h-full"
                hideCustomerProfile={true}
              />
            )
          ) : (
            // Compact empty state when no conversation selected
            <div className="flex-1 bg-slate-50 p-4">
              <div className="h-full flex flex-col">
                <div className="text-center py-8">
                  <Monitor className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Select a Conversation</h3>
                  <p className="text-sm text-gray-600 mb-3">Choose from the sidebar to view chat details</p>
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                    <connectionStatus.icon className="w-3 h-3" />
                    <span>{connectionStatus.label}</span>
                  </div>
                </div>
                
                {/* Quick stats in compact layout */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">12</div>
                      <div className="text-xs text-gray-500">Active</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">2.3m</div>
                      <div className="text-xs text-gray-500">Avg Response</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-600">89%</div>
                      <div className="text-xs text-gray-500">Satisfaction</div>
                    </div>
                  </div>
                </div>
                
                {/* Quick actions in compact layout */}
                <div className="mt-4 space-y-2">
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-900">View All Conversations</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:border-green-300 transition-colors cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-900">Manage Agents</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Right Panel - Customer Intelligence (25% or hidden) */}
        {!customerPanelHidden && (
          <div className={`${panelSizing.customer} min-w-0 flex-shrink-0 border-l border-slate-200`}>
            {selectedConversation ? (
              <CustomerIntelligencePanel
                conversation={selectedConversation}
                customerIntelligence={customerIntelligence}
                currentUser={user}
                viewMode={viewMode}
                className="h-full"
                onAssignmentComplete={(result?: any) => {
                  logger.info('🎯 Assignment completed in Customer Intelligence Panel', {
                    component: 'ProfessionalConversationView',
                    conversationId: selectedConversation?.conversation_id,
                    result,
                    resultKeys: result ? Object.keys(result) : 'no result',
                    selectedConversationAgent: selectedConversation?.assigned_agent_name,
                    // 🔧 DEBUG: Check what's in the assignment result
                    resultAgentName: result?.agentName,
                    resultAssignedAgentName: result?.assignedAgentName,
                    resultAgentId: result?.agentId
                  })
                  
                  // 🔧 FIX: Update local status override for immediate UI feedback (consistent with ConversationDetail)
                  if (result?.status && selectedConversation?.conversation_id) {
                    setLocalStatusOverride({
                      conversationId: selectedConversation.conversation_id,
                      status: result.status,
                      // Handle agent assignment based on action type
                      assignedAgentName: result.status === 'ai_active' || result.status === 'resolved' 
                        ? null 
                        : result.agentName || result.assignedAgentName || selectedConversation.assigned_agent_name,
                      timestamp: Date.now()
                    })
                  }
                  
                  // Refresh the sidebar list after WebSocket updates
                  setTimeout(() => {
                    refetchConversations()
                  }, 1000) // 1 second delay to allow WebSocket updates to complete
                }}
                onPriorityUpdate={handlePriorityUpdate}
              />
            ) : (
              // Empty state for customer intelligence panel
              <div className="h-full flex items-center justify-center bg-gray-50 p-6">
                <div className="text-center text-gray-400">
                  <User className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Select a conversation to view customer intelligence</p>
                </div>
              </div>
            )}
          </div>
        )}
        
      </div>

    </div>
  )
}

export default ProfessionalConversationView